import ApiClient from './apiClient';
import keycloak from '../keycloak';

export interface StaffApiToken {
  id: number;
  name: string | null;
  staff_user_id: number;
  staff_user_email: string;
  token_prefix: string;
  created_at: string;
  updated_at: string;
}

export interface ApiTokenPage {
  items: StaffApiToken[];
  count: number;
}

export type ApiTokenPermissions = Partial<Record<
  'apitokens.list' | 'apitokens.retrieve' | 'apitokens.create' | 'apitokens.destroy', boolean
>>;

interface TokenListResponse {
  objects?: StaffApiToken[];
  results?: StaffApiToken[];
  count?: number;
  totalCount?: number;
}

// The live paginator uses objects/totalCount; OpenAPI documents results/count.
export const normalizeTokenPage = (data: TokenListResponse): ApiTokenPage => {
  const items = data?.objects ?? data?.results;
  const count = data?.count ?? data?.totalCount;
  if (!Array.isArray(items) || !Number.isInteger(count) || (count as number) < 0) {
    throw new Error('Invalid API token list response');
  }
  return { items, count: count as number };
};

const prepare = async () => {
  if (keycloak.authenticated) await keycloak.updateToken(30);
};

const apiTokensService = {
  async permissions(signal?: AbortSignal): Promise<ApiTokenPermissions> {
    await prepare();
    const response = await ApiClient.get<ApiTokenPermissions>('api-tokens/permissions', { signal, timeout: 30000 });
    return response.data;
  },
  async list(params: { mine: boolean; page: number; page_size: number }, signal?: AbortSignal) {
    await prepare();
    const response = await ApiClient.get<TokenListResponse>('api-tokens', { params, signal, timeout: 30000 });
    return normalizeTokenPage(response.data);
  },
  async create(name: string): Promise<StaffApiToken & { token?: string }> {
    await prepare();
    const response = await ApiClient.post<StaffApiToken & { token?: string }>(
      'api-tokens', { name: name.trim() || null }, { timeout: 30000 },
    );
    return response.data;
  },
  async remove(id: number) {
    await prepare();
    // Never automatically replay mutations after a timeout.
    await ApiClient.delete(`api-tokens/${id}`, { timeout: 30000 });
  },
};

export default apiTokensService;
